import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiDownload, FiPlus, FiFileText, FiMenu, FiTrash2, FiCheck, FiX, FiMove, FiEdit } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { resumeApi } from '../../services/api';
import ResumeDocument from './ResumeDocument';
import SectionEditor from './SectionEditor';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ResumeActionTooltip from '../../components/resumes/ResumeActionTooltip';
import ResumeStyleModal from '../../components/resumes/ResumeStyleModal';
import ResumeNameEditor from '../../components/resumes/ResumeNameEditor';

// Version Item Component for the sidebar
const VersionItem = ({ version, resumeId, isActive, onVersionSelect, onVersionDelete, onVersionRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(version.customName || (version.versionId || '').split('_')[0]);
  
  const handleRenameVersion = async (e) => {
    e.stopPropagation();
    try {
      await resumeApi.renameVersion(resumeId, version.versionId, editName);
      onVersionRename(version.versionId, editName);
      setIsEditing(false);
      toast.success('Version renamed successfully');
    } catch (error) {
      console.error('Error renaming version:', error);
      toast.error('Failed to rename version');
    }
  };
  
  const handleDeleteVersion = async (e) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this version? This action cannot be undone.')) {
      try {
        await resumeApi.deleteVersion(resumeId, version.versionId);
        onVersionDelete(version.versionId);
        toast.success('Version deleted successfully');
      } catch (error) {
        console.error('Error deleting version:', error);
        toast.error('Failed to delete version');
      }
    }
  };
  
  return (
    <li 
      className={`px-3 py-2 rounded-md cursor-pointer group ${
        isActive ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100'
      }`}
      onClick={() => !isEditing && onVersionSelect(resumeId, version.versionId)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center flex-grow">
          <FiFileText className="mr-2 h-4 w-4" />
          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="text-sm border border-gray-300 rounded px-1 py-0.5 w-full"
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameVersion(e);
                } else if (e.key === 'Escape') {
                  setIsEditing(false);
                  setEditName(version.customName || (version.versionId || '').split('_')[0]);
                }
              }}
            />
          ) : (
            <span className="text-sm font-medium truncate">
              {version.customName || (version.versionId || '').split('_')[0]}
            </span>
          )}
        </div>
        
        <div className="flex items-center ml-2">
          {isEditing ? (
            <>
              <button 
                onClick={handleRenameVersion}
                aria-label="Save version name"
                className="text-green-600 hover:text-green-800 p-1 transition-all duration-200 rounded-lg hover:bg-green-50"
              >
                <FiCheck className="h-3.5 w-3.5" />
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(false);
                  setEditName(version.customName || (version.versionId || '').split('_')[0]);
                }}
                aria-label="Cancel renaming version"
                className="text-red-600 hover:text-red-800 p-1 transition-all duration-200 rounded-lg hover:bg-red-50"
              >
                <FiX className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                aria-label="Edit version name"
                className="text-gray-500 hover:text-gray-700 p-1 opacity-0 group-hover:opacity-100 transition-all duration-200 rounded-lg hover:bg-gray-50"
              >
                <FiEdit className="h-3.5 w-3.5" />
              </button>
              <button 
                onClick={handleDeleteVersion}
                aria-label="Delete this version"
                className="text-red-500 hover:text-red-700 p-1 opacity-0 group-hover:opacity-100 transition-all duration-200 rounded-lg hover:bg-red-50"
              >
                <FiTrash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="mt-1 flex items-center text-xs text-gray-500">
        <span>
          {new Date(version.updatedAt || version.createdAt).toLocaleDateString()}
        </span>
      </div>
    </li>
  );
};

const ResumeEditor = () => {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeVersion, setActiveVersion] = useState(null);
  const [formData, setFormData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCreateVersionDialog, setShowCreateVersionDialog] = useState(false);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [showActionTooltip, setShowActionTooltip] = useState(false);
  const [showStyleModal, setShowStyleModal] = useState(false);
  
  // States moved from ResumeDocument
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    fetchResume();
  }, [resumeId]);

  const fetchResume = async () => {
    try {
      setLoading(true);
      const response = await resumeApi.getResumeById(resumeId);
      const resumeData = response.data;
      
      setResume(resumeData);
      
      if (resumeData.versions && resumeData.versions.length > 0) {
        const firstVersion = resumeData.versions[0];
        fetchVersionContent(resumeId, firstVersion.versionId);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching resume:', error);
      toast.error('Failed to load resume');
      setLoading(false);
    }
  };

  const fetchVersionContent = useCallback(async (resumeId, versionId) => {
    try {
      setLoadingVersion(true);
      const response = await resumeApi.getResumeVersion(resumeId, versionId);
      setFormData(response.data.content);
      setActiveVersion(response.data);
    } catch (error) {
      console.error('Error fetching version content:', error);
      toast.error('Failed to load resume version');
    } finally {
      setLoadingVersion(false);
      setLoading(false);
    }
  }, []);




  const handleSave = async () => {
    try {
      setSaving(true);
      await resumeApi.updateVersionContent(
        resumeId,
        activeVersion.versionId,
        { content: formData }
      );
      toast.success('Resume saved successfully');
      setEditSection(null);
      setLastSaved(new Date());
      setShowActionTooltip(true);
    } catch (error) {
      console.error('Error saving resume:', error);
      toast.error('Failed to save resume');
    } finally {
      setSaving(false);
    }
  };


  const handleUpdateFormData = (updatedData) => {
    setFormData(updatedData);
  };

  const handleCloseEditor = (save = false) => {
    if (save) {
      handleSave();
    } else {
      setEditSection(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!resume) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Resume not found</h2>
        <p className="mt-2 text-gray-600">The resume you're looking for does not exist.</p>
        <div className="mt-6">
          <Link
            to="/resumes"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FiArrowLeft className="-ml-1 mr-2 h-5 w-5" />
            Back to Resumes
          </Link>
        </div>
      </div>
    );
  }

  // If there's an active version and form data, render the document-like editor interface
  if (activeVersion && formData) {
    return (
      <div className="h-screen flex flex-col">
        {/* Top Navigation Bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between shadow-sm">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/resumes')}
              className="mr-4 text-gray-600 hover:text-gray-900 focus:outline-none"
            >
              <FiArrowLeft className="h-5 w-5" />
            </button>
            <ResumeNameEditor 
              resumeId={resumeId} 
              initialName={resume.name || "Untitled Resume"} 
              onUpdateSuccess={(updatedName) => {
                setResume(prev => prev ? {...prev, name: updatedName} : null);
              }}
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowStyleModal(true)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              title="Download PDF"
            >
              <FiDownload className="mr-1.5 h-4 w-4" />
              Export
            </button>

          </div>
        </div>
        
        {/* Main Content Area with Sidebar and Editor */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - Version Management */}
          {sidebarOpen && (
            <div className="w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Versions</h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setSidebarOpen(!sidebarOpen)}
                      className="p-1 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                      title={sidebarOpen ? "Collapse Versions" : "Expand Versions"}
                    >
                      <FiMenu className="h-4 w-4" />
                    </button>
                    <button
                      className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={() => setShowCreateVersionDialog(true)}
                    >
                      <FiPlus className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                
                {resume.versions && resume.versions.length > 0 ? (
                  <ul className="space-y-2">
                    {resume.versions.map((version) => (
                      <VersionItem 
                        key={version.versionId}
                        version={version}
                        resumeId={resumeId}
                        isActive={activeVersion && activeVersion.versionId === version.versionId}
                        onVersionSelect={fetchVersionContent}
                        onVersionDelete={(versionId) => {
                          // Don't allow deleting if it's the only version
                          if (resume.versions.length <= 1) {
                            toast.error('Cannot delete the only version of a resume');
                            return;
                          }
                          
                          // Update local state to remove the deleted version
                          const updatedVersions = resume.versions.filter(v => v.versionId !== versionId);
                          setResume({...resume, versions: updatedVersions});
                          
                          // If the active version was deleted, switch to another version
                          if (activeVersion && activeVersion.versionId === versionId) {
                            const firstAvailableVersion = updatedVersions[0];
                            if (firstAvailableVersion) {
                              fetchVersionContent(resumeId, firstAvailableVersion.versionId);
                            }
                          }
                        }}
                        onVersionRename={(versionId, newName) => {
                          // Update the version name in the local state
                          const updatedVersions = resume.versions.map(v => 
                            v.versionId === versionId ? {...v, customName: newName} : v
                          );
                          setResume({...resume, versions: updatedVersions});
                        }}
                      />
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-4 text-sm text-gray-500">
                    <p>No versions yet</p>
                    <button
                      onClick={() => setShowCreateVersionDialog(true)}
                      className="mt-2 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                    >
                      <FiPlus className="-ml-0.5 mr-1 h-4 w-4" />
                      Create Version
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Main Editor Area */}
          <div className="flex-1 overflow-auto bg-gray-50">
            {loadingVersion ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
              </div>
            ) : (
              /* Document View with simplified props */
              <ResumeDocument 
                resumeId={resumeId}
                versionId={activeVersion.versionId}
              />
            )}
            
            {/* Section Editor Modal */}
            {editSection && (
              <SectionEditor
                section={editSection}
                formData={formData}
                onSave={handleUpdateFormData}
                onCancel={handleCloseEditor}
              />
            )}
            
            {/* Action Tooltip */}
            <ResumeActionTooltip show={showActionTooltip} resumeId={resumeId} />
            
            {/* Resume Style Modal */}
            <ResumeStyleModal 
              show={showStyleModal}
              onClose={() => setShowStyleModal(false)}
              resumeId={resumeId}
              versionId={activeVersion?.versionId}
            />
          </div>
        </div>
      </div>
    );
  }

  // If no active version, show a message
  return (
    <div className="text-center py-12">
      <h2 className="text-xl font-semibold text-gray-900">No versions found</h2>
      <p className="mt-2 text-gray-600">This resume doesn't have any versions yet.</p>
      <div className="mt-6">
        <button
          onClick={() => setShowCreateVersionDialog(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <FiPlus className="-ml-1 mr-2 h-5 w-5" />
          Create First Version
        </button>
      </div>
    </div>
  );
};

export default ResumeEditor;